
import React, { useState, useEffect, useMemo, memo } from 'react';

interface HeroProps {
    theme: string;
    startWorkspaceWithPrompt: (prompt: string) => void;
}

const StarfieldBackground: React.FC = memo(() => {
    const stars = useMemo(() => {
        const createStars = (count: number, className: string) =>
            Array.from({ length: count }).map((_, i) => (
                <div
                    key={`${className}-${i}`}
                    className={`absolute rounded-full ${className}`}
                    style={{
                        top: `${Math.random() * 100}%`,
                        left: `${Math.random() * 100}%`,
                        animation: `twinkle ${Math.random() * 5 + 3}s linear infinite, moveUp ${Math.random() * 50 + 50}s linear infinite`,
                        animationDelay: `${Math.random() * 5}s`,
                    }}
                />
            ));
        
        return (
            <>
                <div className="absolute inset-0">{createStars(150, 'w-px h-px bg-white/60')}</div>
                <div className="absolute inset-0">{createStars(50, 'w-0.5 h-0.5 bg-white/80')}</div>
                <div className="absolute inset-0">{createStars(20, 'w-1 h-1 bg-white')}</div>
            </>
        );
    }, []);

    return (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <style>{`
                @keyframes twinkle { 0%, 100% { opacity: 0.5; } 50% { opacity: 1; } }
                @keyframes moveUp { 0% { transform: translateY(0); } 100% { transform: translateY(-100vh); } }
            `}</style>
            {stars}
        </div>
    );
});

const CloudySkyBackground: React.FC = memo(() => (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <style>{`
            /* Added a subtle sun glow for more life */
            .sun-glow {
                position: absolute;
                top: 5%;
                left: 10%;
                width: 200px;
                height: 200px;
                background: radial-gradient(circle, rgba(255,255,255,0.4) 0%, rgba(255,255,255,0) 70%);
                border-radius: 50%;
                animation: sun-glow 12s ease-in-out infinite alternate;
            }
            @keyframes sun-glow {
                from { transform: scale(1); opacity: 0.6; }
                to { transform: scale(1.2); opacity: 0.9; }
            }

            /* Reversed animation direction to move right-to-left */
            @keyframes move-clouds {
                0% { transform: translateX(150%); }
                100% { transform: translateX(-150%); }
            }
            .cloud {
                position: absolute;
                background: #ffffff;
                border-radius: 50%;
                animation: move-clouds linear infinite;
            }
            .cloud::before, .cloud::after {
                content: '';
                position: absolute;
                background: #ffffff;
                border-radius: 50%;
            }

            /* Added parallax effect with different layers */
            .cloud-back { opacity: 0.6; filter: blur(6px); }
            .cloud-mid { opacity: 0.8; filter: blur(3px); }
            .cloud-front { opacity: 0.95; filter: blur(1.5px); }

            /* Adjusted existing clouds and added new ones for a fuller sky with depth */
            .cloud1 { width: 200px; height: 60px; top: 15%; animation-duration: 45s; animation-delay: -5s; }
            .cloud2 { width: 150px; height: 45px; top: 30%; animation-duration: 38s; animation-delay: -15s; }
            .cloud3 { width: 250px; height: 70px; top: 60%; animation-duration: 25s; animation-delay: -25s; }
            .cloud4 { width: 180px; height: 50px; top: 45%; animation-duration: 55s; animation-delay: -8s; }
            .cloud5 { width: 220px; height: 65px; top: 80%; animation-duration: 32s; animation-delay: -18s; }
            .cloud6 { width: 170px; height: 55px; top: 5%; animation-duration: 48s; animation-delay: -22s; } /* New */
            .cloud7 { width: 280px; height: 80px; top: 90%; animation-duration: 22s; animation-delay: -10s; } /* New */

            /* Sizing and positioning the fluffy parts for each cloud */
            .cloud1::before { width: 100px; height: 100px; top: -50px; left: 25px; }
            .cloud1::after { width: 150px; height: 150px; top: -75px; right: 25px; }

            .cloud2::before { width: 80px; height: 80px; top: -40px; left: 20px; }
            .cloud2::after { width: 120px; height: 120px; top: -60px; right: 20px; }

            .cloud3::before { width: 120px; height: 120px; top: -60px; left: 30px; }
            .cloud3::after { width: 180px; height: 180px; top: -90px; right: 30px; }
            
            .cloud4::before { width: 90px; height: 90px; top: -45px; left: 22px; }
            .cloud4::after { width: 140px; height: 140px; top: -70px; right: 22px; }

            .cloud5::before { width: 110px; height: 110px; top: -55px; left: 30px; }
            .cloud5::after { width: 170px; height: 170px; top: -85px; right: 30px; }
            
            .cloud6::before { width: 85px; height: 85px; top: -42px; left: 25px; }
            .cloud6::after { width: 130px; height: 130px; top: -65px; right: 25px; }

            .cloud7::before { width: 130px; height: 130px; top: -65px; left: 35px; }
            .cloud7::after { width: 200px; height: 200px; top: -100px; right: 35px; }
        `}</style>
        <div className="sun-glow"></div>
        {/* Back layer (slowest) */}
        <div className="cloud cloud-back cloud4"></div>
        <div className="cloud cloud-back cloud6"></div>
        {/* Mid layer */}
        <div className="cloud cloud-mid cloud1"></div>
        <div className="cloud cloud-mid cloud2"></div>
        <div className="cloud cloud-mid cloud5"></div>
        {/* Front layer (fastest) */}
        <div className="cloud cloud-front cloud3"></div>
        <div className="cloud cloud-front cloud7"></div>
    </div>
));


const Hero: React.FC<HeroProps> = ({ theme, startWorkspaceWithPrompt }) => {
  const [prompt, setPrompt] = useState('');

  const placeholders = useMemo(() => [
    'e.g., A to-do list app with user login and a modern, dark theme...',
    'e.g., A portfolio website for a photographer with a minimalist design...',
    'e.g., A landing page for a new SaaS product with a pricing table...',
    'e.g., A blog for a travel writer with a photo gallery...',
    'e.g., An e-commerce site for handmade jewelry with a shopping cart...',
  ], []);

  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [currentPlaceholder, setCurrentPlaceholder] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fullText = placeholders[placeholderIndex];
    let timeoutId: number;

    if (isDeleting) {
        if (currentPlaceholder.length > 0) {
            timeoutId = window.setTimeout(() => {
                setCurrentPlaceholder(prev => prev.slice(0, -1));
            }, 40); // Faster deleting
        } else {
            setIsDeleting(false);
            setPlaceholderIndex(prev => (prev + 1) % placeholders.length);
        }
    } else {
        if (currentPlaceholder.length < fullText.length) {
            timeoutId = window.setTimeout(() => {
                setCurrentPlaceholder(prev => fullText.slice(0, prev.length + 1));
            }, 80); // Faster typing
        } else {
            timeoutId = window.setTimeout(() => {
                setIsDeleting(true);
            }, 1500); // Shorter pause
        }
    }

    return () => clearTimeout(timeoutId);
  }, [currentPlaceholder, isDeleting, placeholderIndex, placeholders]);

  const handleGenerateClick = () => {
    if (prompt.trim()) {
        startWorkspaceWithPrompt(prompt);
    }
  };

  return (
    <section 
      className="relative min-h-screen flex items-center justify-center overflow-hidden py-24 px-4 text-center"
      style={theme === 'dark' ? { background: 'linear-gradient(180deg, #020c1b 0%, #0a1940 35%, #0f172a 65%, #020c1b 100%)' } : { background: 'linear-gradient(to bottom, #87CEEB, #f5f6fa)' }}
    >
      {theme === 'dark' && <StarfieldBackground />}
      {theme === 'light' && <CloudySkyBackground />}
      
      {/* Subtle bottom glow for dark mode to differentiate from the section below */}
      {theme === 'dark' && (
        <div 
          className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[150%] max-w-[1200px] h-32 z-[1] pointer-events-none"
          style={{
            background: 'radial-gradient(ellipse at 50% 100%, rgba(139, 92, 246, 0.15), transparent 70%)',
          }}
        />
      )}

      <div className="relative z-10 w-full max-w-4xl lg:max-w-5xl">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-[var(--text-primary)] leading-tight">
          Build Apps And Sites in Seconds
        </h1>
        <p className="mt-6 text-lg md:text-xl text-[var(--text-secondary)] max-w-3xl mx-auto">
          Just describe what you need, and let our AI assistant, Codey, bring it to life.
        </p>

        <div className="mt-12 max-w-3xl mx-auto">
          <div className="mt-4 w-full flex flex-col md:block md:relative">
            <textarea
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                className="hero-prompt-textarea w-full p-4 text-base md:p-5 md:pr-44 text-[var(--text-primary)] border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--gradient-from)] focus:outline-none transition-shadow resize-none"
                placeholder={currentPlaceholder}
            />
            <button
                onClick={handleGenerateClick}
                className="mt-4 md:mt-0 w-full md:w-auto md:absolute md:right-3 md:top-1/2 md:-translate-y-1/2 px-6 py-3 md:py-2.5 text-base font-semibold text-white bg-gradient-to-r from-[var(--gradient-from)] to-[var(--gradient-to)] rounded-md hover:opacity-90 transition-opacity"
            >
                Generate Now
            </button>
          </div>
        </div>
      </div>
       <div className="absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-t from-[var(--bg-color)] to-transparent pointer-events-none z-[5]"></div>
    </section>
  );
};

export default Hero;
